// @ts-nocheck
import { getShipTos } from "@insite/client-framework/Services/CustomersService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getBillTosDataView } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import loadBillTos from "@insite/client-framework/Store/Data/BillTos/Handlers/LoadBillTos";
import loadCurrentShipTos from "@insite/client-framework/Store/Data/ShipTos/Handlers/LoadCurrentShipTos";
import { getCurrentShipTosDataView } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ContactFormContext } from "@insite/content-library/Widgets/ContactUs/ContactForm";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import setShipToValue from "@store/Framework/Store/Greif/Pages/ContactUs/Handlers/SetShipToValue";
import React, { ChangeEvent, FC, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";

const enum fields {
    isRequired = "isRequired",
    fieldLabel = "fieldLabel",
    shipTosToDisplay = "shipTosToDisplay",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.isRequired]: boolean;
        [fields.fieldLabel]: string;
        [fields.shipTosToDisplay]: string;
    };
}

const mapStateToProps = (state: ApplicationState, props: OwnProps) => ({
    soldTo: state.greif.pages.contactUs.soldTo,
    shipTo: state.greif.pages.contactUs.shipTo,
    currentShipTosDataView: getCurrentShipTosDataView(state),
    session: getSession(state),
    billToDataview: getBillTosDataView(state, {
        page: 1,
        pageSize: Number(props.fields.shipTosToDisplay),
    }),
});

const mapDispatchToProps = {
    loadCurrentShipTos,
    loadBillTos,
    setShipToValue,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface ShipToStyles {
    shipToSelect?: SelectPresentationProps;
}

export const shipToStyles: ShipToStyles = {};

const styles = shipToStyles;

const ShipTo: FC<Props> = ({
    fields,
    shipTo,
    currentShipTosDataView,
    billToDataview,
    soldTo,
    session,
    setShipToValue,
}) => {
    const { isRequired, fieldLabel, shipTosToDisplay } = fields;
    const contactFormContext = React.useContext(ContactFormContext);
    const [shipToErrorMessage, setShipToErrorMessage] = useState("");
    const [shipTosOptions, setShipTosOptions] = useState<any[]>([]);

    const findSelectedBillto = billToDataview?.value?.find(x => x.customerNumber === soldTo);

    useEffect(() => {
        getShipTos({
            page: 1,
            pageSize: Number(shipTosToDisplay),
            billToId: findSelectedBillto?.id,
            expand: ["validation"],
            exclude: ["showAll"],
        })
            .then(e => {
                setShipTosOptions(e.shipTos || []);
                const haveCurrent = e.shipTos?.find(s => s.id === session.shipToId);

                if (haveCurrent) {
                    setShipToValue({ value: haveCurrent.label });
                }

                if (e.shipTos?.length === 1) {
                    setShipToValue({ value: e.shipTos[0].label });
                }
            })
            .catch(f => {
                if (f) {
                    setShipToErrorMessage("Ship to failed to load");
                }
            });
    }, [currentShipTosDataView, findSelectedBillto]);

    useEffect(() => {
        contactFormContext.validators.shipTo = validateShipTo;
        if (shipTosOptions.length > 0 && typeof shipTo !== "undefined") {
            validateShipTo();
        }
    }, [shipTo]);

    useEffect(() => {
        const currentShipto = shipTosOptions?.find(x => x.id === session.shipToId);
        if (shipTo === "" && currentShipto) {
            setShipToValue({ value: currentShipto?.label });
        }
    }, [shipTosOptions, shipTo]);

    const validateShipTo = () => {
        const errorMessage = isRequired && !shipTo ? (translate(`${fieldLabel} is required`) as string) : "";
        setShipToErrorMessage(errorMessage);
        return !errorMessage;
    };

    const shipToChangeHandler = (event: ChangeEvent<HTMLSelectElement>) => {
        setShipToValue({ value: event.target.value });
    };

    return (
        <Select
            label={translate(fieldLabel || "")}
            value={String(shipTo)}
            onChange={shipToChangeHandler}
            required={isRequired}
            disabled={!shipTosOptions.length}
            error={shipToErrorMessage}
            {...styles.shipToSelect}
        >
            <option value="Other">{translate("Other")}</option>
            {shipTosOptions.map((shipTo, id) => {
                return shipTo.id === session.shipToId ? (
                    <option key={id} value={shipTo.label}>
                        {`[${translate("CURRENT")}] ${shipTo.label}`}
                    </option>
                ) : (
                    <option key={id} value={shipTo.label}>
                        {shipTo.label}
                    </option>
                );
            })}
        </Select>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(ShipTo),
    definition: {
        displayName: "Ship-To",
        group: "Contact Us",
        icon: "input-pipe",
        fieldDefinitions: [
            {
                name: fields.isRequired,
                displayName: "Required Field",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
            {
                name: fields.fieldLabel,
                displayName: "Field Label",
                editorTemplate: "TextField",
                defaultValue: "Ship To",
                fieldType: "Translatable",
            },
            {
                name: fields.shipTosToDisplay,
                displayName: "ShipTos to display",
                editorTemplate: "IntegerField",
                max: 250,
                defaultValue: 250,
                tooltip: "Platform default: 20",
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
